/**
 * Scoped single-escalation view (`ao_escalation_pipeline_e1_e2_build_2026_10_13.md`,
 * todo "Scoped Slack link").
 *
 * The Slack alert for ONE escalation links here (`/escalation/{id}`), NOT to the
 * whole queue, so an operator acting on one page does not have to re-find the row
 * among every queued/dispatched wall. Renders exactly one record — the question,
 * where it stands, and its options — fetched via `GET /api/escalations/{id}`.
 *
 * The view is deliberately "one question + its options": the fields an operator
 * weighs before acting (which wall, where it stands, how hard it has been tried,
 * why it last failed) plus the two authoritative links. It intentionally does
 * NOT embed the queue panel or any neighbouring row.
 */

import { useEffect, useState } from "react";

import { Empty, Panel } from "./components";
import type { EscalationView } from "./types";
import { fmtAgo, fmtDurationBetween } from "./utils";

// ── pure display mappers (vitest-covered) ──────────────────────────────────

const _RED = "var(--status-stale)";
const _AMBER = "var(--status-blocked)";
const _GREEN = "var(--status-working)";
const _MUTE = "var(--fg-2)";

/** Escalation status → colour token. Terminal-good green, still-open red/amber. */
export function escalationStatusTone(status: string): string {
  switch (status) {
    case "resolved":
      return _GREEN;
    case "unresolved":
      return _RED;
    case "abandoned":
      return _MUTE;
    case "queued":
      return _RED;
    case "dispatched":
      return _AMBER;
    default:
      return _MUTE;
  }
}

/**
 * The `EscalationView` id is opaque and may carry URL-hostile characters; the
 * deep-link must survive them (mirrors the server's ``_escalation_deep_link``).
 */
export function scopedEscalationPath(escalationId: string): string {
  return `/escalation/${encodeURIComponent(escalationId)}`;
}

/** One labelled fact about the escalation — the scoped view's whole body. */
export interface EscalationField {
  label: string;
  value: string;
}

/**
 * Exactly the fields a scoped view is allowed to render for one escalation: the
 * question (which wall, where it stands) and its options (the two authoritative
 * surfaces + the timing/attempt context). Deliberately a fixed list — the view
 * must not grow into the queue. ``now`` is injected so the "took" cell is
 * deterministic under test.
 */
export function escalationFields(e: EscalationView, now: number = Date.now()): EscalationField[] {
  const repr = e.pr_number ? `${e.repo}#${e.pr_number}` : e.repo;
  const took = e.dispatched_at
    ? fmtDurationBetween(e.dispatched_at, e.resolved_at ?? new Date(now).toISOString())
    : "—";
  return [
    { label: "Wall", value: `${repr} — ${e.wall_type}` },
    { label: "Status", value: e.status },
    { label: "Opened", value: e.created_at ? fmtAgo(e.created_at) : "—" },
    { label: "Dispatched", value: e.dispatched_at ? fmtAgo(e.dispatched_at) : "not yet" },
    { label: "Took", value: e.resolved_at ? took : e.dispatched_at ? `${took} so far` : "—" },
    { label: "Attempts", value: `${e.attempts} (re-escalated ${e.reescalations}×)` },
    { label: "Dispatched to", value: e.slot_id !== null ? `slot-${e.slot_id}` : "—" },
    { label: "Last error", value: e.last_error ?? "—" },
    { label: "Resolution", value: e.resolution ?? "—" },
  ];
}

// ── view ───────────────────────────────────────────────────────────────────

type LoadState = { kind: "loading" } | { kind: "ok"; data: EscalationView } | { kind: "error"; message: string };

interface EscalationDetailProps {
  baseUrl: string;
  authToken: string | null;
  escalationId: string;
  onBack?: () => void;
}

export function EscalationDetail({ baseUrl, authToken, escalationId, onBack }: EscalationDetailProps) {
  const [load, setLoad] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    async function fetchOne() {
      try {
        const res = await fetch(`${baseUrl}/api/escalations/${encodeURIComponent(escalationId)}`, {
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        });
        if (res.status === 404) {
          if (!cancelled) setLoad({ kind: "error", message: `No escalation ${escalationId}` });
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as EscalationView;
        if (!cancelled) setLoad({ kind: "ok", data });
      } catch (err) {
        if (!cancelled) setLoad({ kind: "error", message: err instanceof Error ? err.message : String(err) });
      }
    }
    void fetchOne();
    return () => {
      cancelled = true;
    };
  }, [baseUrl, authToken, escalationId]);

  const back = onBack ? (
    <button type="button" className="btn btn-sm" onClick={onBack}>
      ← back
    </button>
  ) : null;

  if (load.kind === "loading") {
    return (
      <Panel title={`Escalation ${escalationId}`} right={back} flush>
        <div style={{ padding: 16, color: _MUTE }}>Loading…</div>
      </Panel>
    );
  }
  if (load.kind === "error") {
    return (
      <Panel title={`Escalation ${escalationId}`} right={back} flush tone="blocked">
        <Empty icon={null} title="Escalation not found" sub={load.message} />
      </Panel>
    );
  }

  const e = load.data;
  const tone = escalationStatusTone(e.status);
  return (
    <Panel title={`Escalation ${e.escalation_id}`} right={back} flush tone="blocked">
      <div style={{ padding: 16 }}>
        <div className="question" style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>
          {e.repo}
          {e.pr_number ? `#${e.pr_number}` : ""} — {e.wall_type}
        </div>
        <div style={{ fontSize: 12, color: tone, marginBottom: 12 }}>{e.status}</div>
        {escalationFields(e).map((f) => (
          <div key={f.label} style={{ display: "flex", gap: 12, fontSize: 12, padding: "2px 0" }}>
            <span style={{ minWidth: 110, color: _MUTE }}>{f.label}</span>
            <span style={{ wordBreak: "break-word" }}>{f.value}</span>
          </div>
        ))}
        <div style={{ marginTop: 14, display: "flex", gap: 16, fontSize: 12 }}>
          <a href={`https://github.com/IggyIkenna/${e.repo}`} target="_blank" rel="noreferrer">
            open {e.repo} on GitHub
          </a>
          <a
            href={`${baseUrl}/api/escalations/${encodeURIComponent(e.escalation_id)}`}
            target="_blank"
            rel="noreferrer"
          >
            raw record (API)
          </a>
        </div>
      </div>
    </Panel>
  );
}
